import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import * as ActionTypes from '@storeOld/actions'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { THEME_BLUEPRINT } from '@/constants/constants'
import { hasFilledText, lightOrDark } from '@/utils'

const MAX_DISPLAYED_COMMENTS_NUMBER = 999

export const useNote = (blockId: ID) => {
  const dispatch = useDispatch()

  const isAutoCreated = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.autoCreatedBlockId === blockId
  )

  const isEmbed = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.isEmbed
  )

  const block = useSelector(
    migrationProjectSelector.getProjectBlockOrSymbolData(blockId),
    shallowEqual
  )

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(block.colorId)
  )

  const blockCommentsCount = useSelector<AppState, number>(({ manager }) => {
    return manager.comments.filter((comment) => comment.commentable === blockId)
      .length
  })

  const theme = useSelector(migrationProjectSelector.getProjectTheme)

  const hasComments = blockCommentsCount > 0

  const hasContent = hasFilledText(block.content)
  const themeBlueprint = theme === THEME_BLUEPRINT
  const commentsCount = Math.min(
    blockCommentsCount,
    MAX_DISPLAYED_COMMENTS_NUMBER
  )
  const hideComponent = isAutoCreated || (isEmbed && commentsCount === 0)
  const light = lightOrDark(colorHex) === 'light'
  const hasImage = block.visual.type === 'image' && !block.title

  const contentOnly = hasContent && !hasComments
  const commentsOnly = hasComments && !hasContent
  const contentWithComments = hasContent && hasComments
  const empty = !hasContent && !hasComments

  const disabled = isEmbed

  const onClick = () => {
    if (!isEmbed) {
      dispatch({
        type: ActionTypes.OPEN_BLOCK,
        payload: { id: blockId, scrolless: true },
      })
    }
  }

  return {
    disabled,
    commentsCount,
    themeBlueprint,
    hideComponent,
    light,
    hasImage,
    contentOnly,
    commentsOnly,
    contentWithComments,
    empty,
    onClick,
  }
}
